import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api/api";
import { ArrowLeft, Save, Pencil, X } from "lucide-react";

const STATUS_OPTIONS = ["Open", "Cancelled", "Closed"];

export default function SchedulePayment() {
  const { purchase_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Which existing row is currently being edited (by its id)
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingRow, setSavingRow] = useState(false);

  const [form, setForm] = useState({
    date: "",
    mode: "Cheque",
    modeDetails: "",
    amount: "",
    status: "No",
    scheduleStatus: "Open",
  });

  useEffect(() => {
    fetchRows();
  }, [purchase_id]);

  const fetchRows = async () => {
    try {
      const res = await api.get(`/schedule-payment/list/${purchase_id}`);
      setRows(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const submit = async () => {
    if (!form.date || !form.amount) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setLoading(true);

      await api.post("/schedule-payment/add", {
        purchase_id,
        date: form.date,
        mode: form.mode,
        mode_details: form.modeDetails,
        amount: form.amount,
        status: form.status,
        schedule_status: form.scheduleStatus,
      });

      setForm({
        date: "",
        mode: "Cheque",
        modeDetails: "",
        amount: "",
        status: "No",
        scheduleStatus: "Open",
      });

      fetchRows();
    } catch (err) {
      console.error(err);
      alert("Failed to save.");
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (row) => {
    const rowId = row.payment_shecule_id;

    setEditingId(rowId);
    setEditForm({
      date: row.date || "",
      mode: row.mode || "Cheque",
      modeDetails: row.mode_details || "",
      amount: row.amount || "",
      status: row.status || "No",
      scheduleStatus: row.schedule_status || "Open",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEditing = async (rowId) => {
    if (!editForm.date || !editForm.amount) {
      alert("Please fill all fields.");
      return;
    }

    try {
      setSavingRow(true);

      await api.put(`/schedule-payment/update/${rowId}`, {
        date: editForm.date,
        mode: editForm.mode,
        mode_details: editForm.modeDetails,
        amount: editForm.amount,
        status: editForm.status,
        schedule_status: editForm.scheduleStatus,
      });

      setEditingId(null);
      setEditForm(null);

      fetchRows();
    } catch (err) {
      console.error(err);
      alert("Failed to update.");
    } finally {
      setSavingRow(false);
    }
  };

  return (
    <div className="ml-[68px] pt-16 p-6 bg-[#F4F7FB] min-h-screen">

      <div className="flex justify-between items-center mb-6">

        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="border rounded-md p-2 hover:bg-gray-100"
          >
            <ArrowLeft size={18} />
          </button>

          <div>
            <h1 className="text-2xl font-semibold">
              Schedule Payment
            </h1>

            <p className="text-sm text-gray-500">
              Purchase ID : {purchase_id}
            </p>
          </div>
        </div>

      </div>

      <div className="bg-white rounded-lg shadow border overflow-hidden overflow-x-auto">

        <table className="w-full">

          <thead className="bg-gray-100">

            <tr>

              <th className="text-left p-3">Date</th>

              <th className="text-left p-3">Mode</th>

              <th className="text-left p-3">Mode Details</th>

              <th className="text-left p-3">Amount</th>

              <th className="text-left p-3">Paid</th>

              <th className="text-left p-3">Status</th>

              <th className="text-left p-3">Action</th>

            </tr>

          </thead>

          <tbody>

            {/* Input Row (new entry) */}

            <tr className="border-t">

              <td className="p-2">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      date: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">

                <select
                  value={form.mode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      mode: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                >
                  <option>Cheque</option>
                  <option>Cash</option>
                  <option>Other</option>
                </select>

              </td>

              <td className="p-2">
                <input
                  type="text"
                  placeholder="e.g. Cheque No / UTR"
                  value={form.modeDetails}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      modeDetails: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />
              </td>

              <td className="p-2">

                <input
                  type="number"
                  value={form.amount}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      amount: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                />

              </td>

              <td className="p-2">

                <select
                  value={form.status}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                >
                  <option>No</option>
                  <option>Yes</option>
                </select>

              </td>

              <td className="p-2">
                <select
                  value={form.scheduleStatus}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      scheduleStatus: e.target.value,
                    })
                  }
                  className="border rounded-md px-3 py-2 w-full"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt}>{opt}</option>
                  ))}
                </select>
              </td>

              <td className="p-2">

                <button
                  onClick={submit}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                >
                  <Save size={16} />

                  {loading ? "Saving..." : "Submit"}

                </button>

              </td>

            </tr>

            {/* Existing Rows (editable) */}

            {rows.map((row) => {
              const rowId = row.payment_shecule_id;
              const isEditing = editingId === rowId;

              return (
                <tr key={rowId} className="border-t">

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="date"
                        value={editForm.date}
                        onChange={(e) =>
                          setEditForm({ ...editForm, date: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{row.date}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <select
                        value={editForm.mode}
                        onChange={(e) =>
                          setEditForm({ ...editForm, mode: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      >
                        <option>Cheque</option>
                        <option>Cash</option>
                        <option>Other</option>
                      </select>
                    ) : (
                      <span className="p-1">{row.mode}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.modeDetails}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            modeDetails: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">{row.mode_details || "—"}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editForm.amount}
                        onChange={(e) =>
                          setEditForm({ ...editForm, amount: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      />
                    ) : (
                      <span className="p-1">₹{row.amount}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <select
                        value={editForm.status}
                        onChange={(e) =>
                          setEditForm({ ...editForm, status: e.target.value })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select>
                    ) : (
                      <span className="p-1">{row.status}</span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <select
                        value={editForm.scheduleStatus}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            scheduleStatus: e.target.value,
                          })
                        }
                        className="border rounded-md px-3 py-2 w-full"
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          row.schedule_status === "Closed"
                            ? "bg-green-100 text-green-700"
                            : row.schedule_status === "Cancelled"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {row.schedule_status || "Open"}
                      </span>
                    )}
                  </td>

                  <td className="p-2">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => saveEditing(rowId)}
                          disabled={savingRow}
                          className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm"
                        >
                          <Save size={14} />
                          {savingRow ? "Saving..." : "Save"}
                        </button>

                        <button
                          onClick={cancelEditing}
                          className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditing(row)}
                        className="flex items-center gap-1 border px-3 py-2 rounded-md text-sm hover:bg-gray-50"
                      >
                        <Pencil size={14} />
                        Edit
                      </button>
                    )}
                  </td>

                </tr>
              );
            })}

          </tbody>

        </table>

      </div>

    </div>
  );
}